/**
 * 작성자 : 홍선영
 * 날짜 : 2023.11.28
 * 경로 : 설정관리-장비관리-센서 측정 범위 관리 (현장관리자)
 */
/**
 * 수정자 : 박승호
 * 날짜 : 2024.10.14
 * 수정 이유 : InfoTextWithIcon => TextInfoBox 컴포넌트로 변경
 */

import { useEffect, useState } from 'react';
import styled from 'styled-components';

import i18n from '../../../translation/i18n';
import { ContentsContainerRoot } from '../../../assets/styles/ContentsContainerRoot';
import { SearchOptions } from '../../../assets/styles/SearchOptions';
import { BtnBlue, BtnGhost, BtnRed } from '../../../components/Button';
import { COMCD_SENSOR_RANGE, COMCD_USE_YN, INIT_USE_YN_A, SENSOR_HINT, SENSOR_UNIT_LIST } from '../../../_constants';
import { setComCdListState } from '../../../utils/setComCdListState';
import { IComCdList, IModal, ISensorRange } from 'customTypes';
import SelectBox from '../../../components/SelectBox';
import Input from '../../../components/Input';
import { useQuery } from '@tanstack/react-query';
import { useRecoilValue } from 'recoil';
import { IUser, userState } from '../../../atoms';
import { InputTable } from '../../../assets/styles/InputTable';
import SelectBoxs from '../../../components/SelectBoxs';
import { toast } from 'react-toastify';
import Portal from '../../../components/Portal';
import DeleteModal from '../../../components/Modal/DeleteModal2';
import { useSetAuth } from '../../../utils/useSetAuth';
import { useTranslation } from 'react-i18next';
import { applyBorderStyle } from '../../../utils/applyBorderStyle';
import { apiGet, apiPost } from '../../../services/_common';
import { useFetchCommonCodeList } from '../../../services/useSetCodeListInSelectBoxForm';
import { logPost } from '../../../services/log';
import TextInfoBox from '../../../components/TextInfoBox';
import { useOutletContext } from 'react-router-dom';

const ButtonsWrapper = styled.div`
  border-top: 1px solid var(--outline);
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  padding: 0.5rem;
  button {
    height: 2.5rem;
    font-size: 0.875rem;
  }
`;

const Root = styled(ContentsContainerRoot)`
  .secondSearchOption {
    width: fit-content;
  }

  ul > li {
    width: 100% !important;
  }

  .textBtnGroup button {
    padding: 0 0.75rem;
    width: 4.5rem;
    height: 2.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    border: 1px solid var(--outline);
    background-color: var(--tonal-light);
  }

  .o2Select {
    overflow: visible !important;
    position: relative;
    z-index: 1;
    > div {
      width: 90% !important;
    }
  }

  .trCol15 {
    padding: 0 1rem !important;
    > div {
      width: 100% !important;
    }
  }

  .tr {
    @media (max-width: 768px) {
      > div:nth-child(4),
      > div:nth-child(5),
      > div:nth-child(6) {
        width: 18rem;
      }

      > div > div {
        width: 100%;
      }
    }
  }
`;

const SensorRangeSetting = () => {
  const { t } = useTranslation();
  const { auth } = useSetAuth(); // 사용자 권한값 훅
  const size = useOutletContext<any>();
  const userInfo = useRecoilValue<IUser>(userState);
  const [loading, setLoading] = useState(false);
  const [searchOptionUseYn, setSearchOptionUseYn] = useState(INIT_USE_YN_A);
  const [searchOption, setSearchOption] = useState({ sensorName: '' });
  const [table, setTable] = useState<ISensorRange[]>([]);
  const [initTable, setInitTable] = useState<ISensorRange[]>([]);
  const [comCdSensorRangeList, setComCdSensorRangeList] = useState<IComCdList[]>([]);
  const [comCdSensorRanges, setComCdSensorRanges] = useState<any[]>([]);
  const [isSaveClicked, setIsSaveClicked] = useState(false); // 저장버튼 클릭 여부에 따라 필수입력값 보더색상 처리하기 위한 state 값
  const [openModal, setOpenModal] = useState<IModal>({ status: false, type: '', title: '' });
  const sortOrder = ['O2', 'NO2', 'CO', 'CO2', 'H2S', 'CH4', 'CH2O', 'O3', 'PM2.5', 'PM10', 'VOC', 'PM1'];
  const [isFirstInput, setIsFirstInput] = useState(false);
  const [isO2Del, setIsO2Del] = useState(false);
  const [o2UseYn, setO2UseYn] = useState({ type: COMCD_USE_YN, [COMCD_USE_YN]: 'Y', cdName: t('사용') });
  const { data: useYnComCdListWithAll } = useFetchCommonCodeList(COMCD_USE_YN, true); // 사용여부 공통코드 목록 (전체포함)
  const { data: useYnComCdList } = useFetchCommonCodeList(COMCD_USE_YN, false); // 사용유무 공통코드 목록

  // 센서별 기본 단위 정의
  const getDefaultUnit = (srCd: string): string => SENSOR_UNIT_LIST[srCd] || 'ppm';

  // CO2 단위 옵션
  const co2UnitOptions = [
    { type: 'unit', unit: 'ppm', cdName: 'ppm' },
    { type: 'unit', unit: '%', cdName: '%' },
    { type: 'unit', unit: 'mg/m³', cdName: 'mg/m³' },
  ];

  const sensorRangeQuery = useQuery(['sensorRangeGet', userInfo.hCd, userInfo.sCd], () => apiGet({ path: '/sensor/range', req: { hCd: userInfo.hCd, sCd: userInfo.sCd } }), {
    enabled: !!userInfo.hCd && !!userInfo.sCd,
  });

  useEffect(() => {
    logPost({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      userId: userInfo.userId,
      menu: '장비 관리 > 센서 측정 범위 관리',
      action: '조회',
      etc: ``,
    });
  }, []);

  useEffect(() => {
    setSearchOptionUseYn(INIT_USE_YN_A);
  }, [i18n.language]);

  useEffect(() => {
    setComCdListState(COMCD_SENSOR_RANGE, setComCdSensorRangeList, false);
  }, []);

  useEffect(() => {
    if (comCdSensorRangeList) {
      const newSensorList = comCdSensorRangeList
        .map((el: any) => ({
          srCd: el[COMCD_SENSOR_RANGE],
          level1: '',
          level2: '',
          level3: '',
          useYn: 'Y',
          sName: el.cdName,
          delYn: 'N',
          unit: getDefaultUnit(el[COMCD_SENSOR_RANGE]), // 기본 단위 설정
        }))
        .filter((el2: any) => el2.srCd !== 'O2' && el2.srCd !== 'TEMP' && el2.srCd !== 'HUMI' && el2.srCd !== 'FEELS');
      setComCdSensorRanges(newSensorList);
    }
  }, [comCdSensorRangeList]);

  useEffect(() => {
    if (sensorRangeQuery.isSuccess && sensorRangeQuery.data.status === 200) {
      const { rangeList } = sensorRangeQuery.data.data.data;
      if (comCdSensorRanges.length > 0 && rangeList.length > 0) {
        const ar = comCdSensorRanges.map((comRg: any) => {
          const findRange = rangeList.find((rg: any) => rg.srCd === comRg.srCd && comRg.delYn === 'N');
          if (findRange) {
            return {
              ...findRange,
              unit: findRange.unit || getDefaultUnit(comRg.srCd), // 기존 데이터에 단위가 없으면 기본 단위 설정
            };
          }
          return {
            srCd: comRg.srCd,
            level1: '',
            level2: '',
            level3: '',
            useYn: 'Y',
            sName: comRg.sName,
            delYn: 'N',
            unit: getDefaultUnit(comRg.srCd), // 기본 단위 설정
          };
        });

        let newArray = ar;

        // 목록에서 산소값 제외 (고정값 넣기 위함)
        const findO2 = ar.find((el: any) => el.srCd === 'O2');
        if (findO2) {
          const removeO2 = ar.filter((el: any) => el.srCd !== 'O2');
          newArray = removeO2;
          setO2UseYn({ type: COMCD_USE_YN, [COMCD_USE_YN]: findO2.useYn, cdName: findO2.useYn === 'Y' ? t('사용') : t('미사용') });
          if (findO2.delYn === 'Y') setIsO2Del(true);
          else setIsO2Del(false);
        }

        const sortedData = sortTableOrder(newArray);

        const filterDelYnN = newArray.filter((el: ISensorRange) => el.delYn === 'N');
        if (filterDelYnN.length > 0) {
          setTable(sortedData);
          setInitTable(sortedData);
        } else {
          setIsFirstInput(false);
        }
      } else {
        // 최초로 입력하는 경우, 기본값을 가져옴
        setIsFirstInput(true);
        const defaultRanges = comCdSensorRanges.map((el: any) => ({
          ...el,
          unit: el.unit || getDefaultUnit(el.srCd),
        }));
        setTable(defaultRanges);
        setInitTable(defaultRanges);
      }
    }
  }, [sensorRangeQuery.isSuccess, sensorRangeQuery.isRefetching, comCdSensorRanges]);

  const sortTableOrder = (array: ISensorRange[]) => {
    array.sort((a: ISensorRange, b: ISensorRange) => {
      const indexA = sortOrder.indexOf(a.srCd);
      const indexB = sortOrder.indexOf(b.srCd);

      return indexA - indexB;
    });
    return array;
  };

  // 초기화 버튼 클릭
  const onClickInitiateSearchOption = () => {
    Object.keys(searchOption).map((el: any) => setSearchOption((prev: any) => ({ ...prev, [el]: '' })));
    setSearchOptionUseYn(INIT_USE_YN_A);
  };

  const onClickSave = () => {
    setIsSaveClicked(true);
    const newArray = table.map((el: any) => ({
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      srCd: el.srCd,
      level1: el.level1,
      level2: el.level2,
      level3: el.level3,
      useYn: el.useYn,
      delYn: el.delYn,
      unit: el.unit || getDefaultUnit(el.srCd), // 단위 정보 포함
      writer: userInfo.userId,
      editor: userInfo.userId,
    }));
    const delO2 = newArray.filter((v: any) => v.srCd !== 'O2');
    const filterDel = delO2.filter((el: ISensorRange) => el.delYn === 'N');
    const emptyCheck = filterDel.filter((el: any) => {
      const required = ['level1', 'level2', 'level3']; // 필수입력값 name
      const check = required.find((el2: any) => el.useYn === 'Y' && el[el2] === '');
      return check;
    });

    if (emptyCheck.length > 0) toast.warning(t('필수입력값을 모두 입력하세요'));
    else saveSensorRangeAPI(newArray);
  };

  const saveSensorRangeAPI = async (sensorRangeListParam: ISensorRange[]) => {
    const defaultO2Value = {
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      srCd: 'O2',
      level1: '19',
      level2: '0.5',
      level3: '22.5',
      useYn: o2UseYn[COMCD_USE_YN],
      delYn: 'N',
      writer: userInfo.userId,
      editor: userInfo.userId,
      unit: getDefaultUnit('O2'),
    };

    const req = { sensorRangeReqDto: [defaultO2Value, ...sensorRangeListParam] };
    const res = await apiPost({ path: '/sensor/range', req });
    const { data, statusCode, message } = res.data;
    if (statusCode === 200) {
      setIsFirstInput(false);
      toast.success(t(message));

      let newArray = data.rangeList;

      // 목록에서 산소값 제외 (고정값 넣기 위함)
      const findO2 = newArray.find((el: any) => el.srCd === 'O2');
      if (findO2) {
        const removeO2 = newArray.filter((el: any) => el.srCd !== 'O2');
        newArray = removeO2;
        setO2UseYn({ type: COMCD_USE_YN, [COMCD_USE_YN]: findO2.useYn, cdName: findO2.useYn === 'Y' ? t('사용') : t('미사용') });
        if (findO2.delYn === 'Y') setIsO2Del(true);
        else setIsO2Del(false);
      }

      const sortedData = sortTableOrder(newArray);
      const sortedWithUnit = sortedData.map((el: any) => ({
        ...el,
        unit: el.unit || getDefaultUnit(el.srCd),
      }));
      setTable(sortedWithUnit);
      setInitTable(sortedWithUnit);
    }
  };

  const deleteAPI = async (deleteObj: any) => {
    const req = {
      sensorRangeReqDto: table.map((el: ISensorRange) =>
        el.srCd === deleteObj.srCd
          ? {
              hCd: userInfo.hCd,
              sCd: userInfo.sCd,
              srCd: el.srCd,
              level1: el.level1,
              level2: el.level2,
              level3: el.level3,
              useYn: el.useYn,
              delYn: 'Y',
              writer: userInfo.userId,
              editor: userInfo.userId,
            }
          : {
              hCd: userInfo.hCd,
              sCd: userInfo.sCd,
              srCd: el.srCd,
              level1: el.level1,
              level2: el.level2,
              level3: el.level3,
              useYn: el.useYn,
              delYn: el.delYn,
              writer: userInfo.userId,
              editor: userInfo.userId,
            }
      ),
    };
    const res = await apiPost({ path: '/sensor/range', req });
    // const res = await sensorRangePost(reqData);
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      toast.success(t('센서 범위 목록 삭제 성공'));

      if (data.rangeList.length > 0) {
        const filterDelYnN = data.rangeList.filter((el: ISensorRange) => el.delYn === 'N');
        const sortedData = sortTableOrder(filterDelYnN);
        const sortedWithUnit = sortedData.map((el: any) => ({
          ...el,
          unit: el.unit || getDefaultUnit(el.srCd),
        }));
        setTable(sortedWithUnit);
        setInitTable(sortedWithUnit);
      } else {
        setIsFirstInput(false);
      }
    }
  };

  const deleteO2API = async (deleteObj: any) => {
    const req = { sensorRangeReqDto: [deleteObj] };
    const res = await apiPost({ path: '/sensor/range', req });
    const { data, statusCode } = res.data;
    if (statusCode === 200) {
      toast.success(t('센서 범위 목록 삭제 성공'));
      setIsO2Del(true);
    }
  };

  const onClickDelete = (delObj: any, index: number) => {
    if (!isFirstInput) {
      const data = { ...delObj, editor: userInfo.userId }; // 삭제 시 에디터아이디 추가
      setOpenModal((prev: any) => ({ ...prev, type: 'delete', status: true, table, setTable, api: () => deleteAPI(data), el: data, index }));
    } else {
      const newArray = table.map((el: ISensorRange) => (el.srCd === delObj.srCd ? { ...delObj, delYn: 'Y' } : { ...el }));
      setTable(newArray);
    }
  };

  const onClickDeleteO2 = () => {
    const defaultO2Value = {
      hCd: userInfo.hCd,
      sCd: userInfo.sCd,
      srCd: 'O2',
      level1: '19',
      level2: '0.5',
      level3: '22.5',
      useYn: o2UseYn[COMCD_USE_YN],
      delYn: 'Y',
      writer: userInfo.userId,
      editor: userInfo.userId,
    };
    if (!isFirstInput) {
      setOpenModal((prev: any) => ({ ...prev, type: 'delete', status: true, table, setTable, api: () => deleteO2API(defaultO2Value), el: defaultO2Value }));
    }
  };

  useEffect(() => {
    // 검색옵션 변경됐을 때 필터링 처리
    // 필터링된 어레이 리턴, 대소문자구분X
    const filteredArray = initTable.filter((item: any) => {
      return item.sName?.toLowerCase()?.includes(searchOption.sensorName?.toLowerCase());
    });

    const result = YNFilter(filteredArray, 'useYn', searchOptionUseYn[COMCD_USE_YN]);
    if (result.length > 0) setTable(result);
    else setTable([]);
  }, [searchOption.sensorName, searchOptionUseYn[COMCD_USE_YN]]);

  const YNFilter = (initialArray: ISensorRange[], key: string, option: any) => {
    if (option === 'Y') {
      return initialArray.filter((el: any) => el[key] === 'Y');
    }
    if (option === 'N') {
      return initialArray.filter((el: any) => el[key] === 'N');
    }
    return initialArray;
  };

  return (
    <Root loading={loading}>
      <SearchOptions>
        {size.innerSize.W > 1024 ? (
          <div className='inputsWrapper'>
            <div className='flex-basic textBtnGroup'>
              <BtnGhost onClick={onClickInitiateSearchOption}>{t('초기화')}</BtnGhost>
            </div>
            <div className='inputForm-col withLabelComCf'>
              <label htmlFor='searchOptionUseYn'>{t('사용유무')}</label>
              <SelectBox
                options={useYnComCdListWithAll}
                defaultOption={t('전체')}
                state={searchOptionUseYn}
                setState={setSearchOptionUseYn}
                stateKey={COMCD_USE_YN}
                initiateKey={searchOptionUseYn[COMCD_USE_YN]}
                filterbar='filter-1-left'
              />
            </div>
            <div className='inputForm-row'>
              <div className='inputForm-col'>
                <Input placeholder={t('센서명')} label='' type='text' id='sensorName' name='sensorName' state={searchOption} setState={setSearchOption} />
              </div>
            </div>
          </div>
        ) : (
          <div className='filter-wrapper'>
            <div className='inputsWrapper'>
              <div className='inputForm-row' style={{ width: '100%' }}>
                <div className='inputForm-col'>
                  <Input placeholder={t('센서명')} label='' type='text' id='sensorName' name='sensorName' state={searchOption} setState={setSearchOption} ico='search' />
                </div>
              </div>
            </div>
          </div>
        )}
      </SearchOptions>
      <TextInfoBox ico='lightbulb' description='O2(산소)는 입력 불필요' color='orange' />
      <InputTable>
        <div className='thead'>
          <div className='tr'>
            <div className='trCol2p5 flex-center tableStickyNo'>No</div>
            <div className='trCol6 flex-center'>{t('코드')}</div>
            <div className='trCol8 flex-center'>{t('센서명')}</div>
            <div className='trCol15 flex-center content-overflow required'>{t('주의')}</div>
            <div className='trCol15 flex-center content-overflow required'>{t('경고')}</div>
            <div className='trCol15 flex-center content-overflow required'>{t('위험')}</div>
            <div className='trCol10 flex-center'>{t('단위')}</div>
            <div className='trCol10 flex-center'>{t('사용유무')}</div>
            <div className='trCol6 flex-basic shrink0 padding-right-05'> </div>
          </div>
        </div>
        <div className='table'>
          <div className='tbody'>
            {!isO2Del && (
              <div className='tr' role='button' tabIndex={0}>
                <div className='trCol2p5 flex-center tableStickyNo'>1</div>
                <div className='trCol6 flex-center'>O2</div>
                <div className='trCol8 flex-center'>{t('산소')}</div>
                <div className='trCol15 flex-center content-overflow'>
                  <div style={{ textAlign: 'center', width: '100%' }}>
                    <div>{t('하한가')} : 18.5% ~ 19%</div>
                    <div>{t('상한가')} : 22.5% ~ 23%</div>
                  </div>
                </div>
                <div className='trCol15 flex-center content-overflow'>
                  <div style={{ textAlign: 'center', width: '100%' }}>
                    <div>{t('하한가')} : 18% ~ 18.5%</div>
                    <div>{t('상한가')} : 23% ~ 23.5%</div>
                  </div>
                </div>
                <div className='trCol15 flex-center content-overflow'>
                  <div style={{ textAlign: 'center', width: '100%' }}>
                    <div>
                      {t('하한가')} : 18% {t('미만')}
                    </div>
                    <div>
                      {t('상한가')} : 23.5% {t('초과')}
                    </div>
                  </div>
                </div>
                <div className='trCol10 flex-center'>
                  <SelectBoxs
                    options={[{ type: 'unit', unit: '% vol', cdName: '% vol' }]}
                    defaultOption='% vol'
                    state={[{ unit: '% vol' }]}
                    setState={() => {}}
                    rawData={[{ unit: '% vol' }]}
                    setRawData={() => {}}
                    stateKey='unit'
                    codeKey='cdName'
                    index={0}
                    object={{ unit: '% vol' }}
                    primaryKey='unit'
                    width='90%'
                    disabled
                  />
                </div>
                <div className='trCol10 flex-center o2Select'>
                  <SelectBox
                    options={useYnComCdList}
                    defaultOption={o2UseYn[COMCD_USE_YN] === 'Y' ? t('사용') : t('미사용')}
                    state={o2UseYn}
                    setState={setO2UseYn}
                    stateKey={COMCD_USE_YN}
                    initiateKey={o2UseYn[COMCD_USE_YN]}
                    filterbar='filter-1-left'
                    dropDownWidth='fit-content'
                  />
                </div>
                {auth.deleteAuth && (
                  <div className='trCol6 flex-center shrink0 padding-right-05'>
                    <BtnRed onClick={onClickDeleteO2}>{isFirstInput ? t('제거') : t('삭제')}</BtnRed>
                  </div>
                )}
              </div>
            )}

            {table?.length > 0 ? (
              table.map((el: ISensorRange, i: number) => {
                if (el.delYn !== 'Y') {
                  return (
                    <div className='tr' role='button' tabIndex={0} key={`sensor_${i}`}>
                      <div className='trCol2p5 flex-center tableStickyNo'>{isO2Del ? i + 1 : i + 2}</div>
                      <div className='trCol6 flex-center'>{el.srCd}</div>
                      <div className='trCol8 flex-center'>{el.sName}</div>
                      <div className='trCol15 flex-center content-overflow'>
                        <Input
                          name='level1'
                          type='input'
                          state={table}
                          setState={setTable}
                          className='floatingPlaceholder'
                          label={
                            el.srCd === 'CO2' && (el as any).unit === '%'
                              ? '0.1% - 0.4% (예: 0.3)'
                              : el.srCd === 'CO2' && (el as any).unit === 'mg/m³'
                              ? '1,800 mg/m³ - 5,399 mg/m³ (예: 3600)'
                              : SENSOR_HINT[el.srCd]?.level1
                          }
                          floatingPlaceholder
                          trim
                          stateType={{ type: 'array', index: i }}
                          valueType='decimal'
                          disabled={!auth.createAuth && !auth.updateAuth}
                          getBorderStyle={isSaveClicked && el.useYn === 'Y' ? applyBorderStyle(el.level1, 'red', 'level1') : undefined}
                        />
                        <span style={{ padding: '0 0.5rem' }}>{t('이상')}</span>
                      </div>
                      <div className='trCol15 flex-center content-overflow'>
                        <Input
                          name='level2'
                          type='input'
                          state={table}
                          setState={setTable}
                          className='floatingPlaceholder'
                          label={
                            el.srCd === 'CO2' && (el as any).unit === '%'
                              ? '0.5% - 1.4% (예: 1.0)'
                              : el.srCd === 'CO2' && (el as any).unit === 'mg/m³'
                              ? '5,400 mg/m³ - 8,999 mg/m³ (예: 7200)'
                              : SENSOR_HINT[el.srCd]?.level2
                          }
                          floatingPlaceholder
                          trim
                          stateType={{ type: 'array', index: i }}
                          valueType='decimal'
                          disabled={!auth.createAuth && !auth.updateAuth}
                          getBorderStyle={isSaveClicked && el.useYn === 'Y' ? applyBorderStyle(el.level2, 'red', 'level2') : undefined}
                        />
                        <span style={{ padding: '0 0.5rem' }}>{t('이상')}</span>
                      </div>
                      <div className='trCol15 flex-center content-overflow'>
                        <Input
                          name='level3'
                          type='input'
                          state={table}
                          setState={setTable}
                          className='floatingPlaceholder'
                          label={
                            el.srCd === 'CO2' && (el as any).unit === '%'
                              ? '1.5% 초과 (예: 1.6)'
                              : el.srCd === 'CO2' && (el as any).unit === 'mg/m³'
                              ? '9,000 mg/m³ 초과 (예: 9001)'
                              : SENSOR_HINT[el.srCd]?.level3
                          }
                          floatingPlaceholder
                          trim
                          stateType={{ type: 'array', index: i }}
                          valueType='decimal'
                          disabled={!auth.createAuth && !auth.updateAuth}
                          getBorderStyle={isSaveClicked && el.useYn === 'Y' ? applyBorderStyle(el.level3, 'red', 'level3') : undefined}
                        />
                        <span style={{ padding: '0 0.5rem' }}>{t('이상')}</span>
                      </div>
                      <div className='trCol10 flex-center'>
                        {el.srCd === 'CO2' ? (
                          <SelectBoxs
                            options={co2UnitOptions}
                            defaultOption={co2UnitOptions.find((opt: any) => opt.unit === ((el as any).unit || 'ppm'))?.cdName || 'ppm'}
                            state={table}
                            setState={(newState: any) => {
                              const newTable = [...newState];
                              const co2Item = newTable[i];
                              if (co2Item && co2Item.srCd === 'CO2') {
                                const oldItem = table[i];
                                const oldUnit = (oldItem as any)?.unit || 'ppm';
                                const newUnit = (co2Item as any).unit || 'ppm';

                                // 단위가 변경되면 입력값 초기화
                                if (oldUnit !== newUnit) {
                                  newTable[i] = {
                                    ...co2Item,
                                    level1: '',
                                    level2: '',
                                    level3: '',
                                  };

                                  const newInitTable = [...initTable];
                                  if (newInitTable[i]) {
                                    newInitTable[i] = {
                                      ...newInitTable[i],
                                      level1: '',
                                      level2: '',
                                      level3: '',
                                    };
                                  }
                                  setInitTable(newInitTable);
                                }
                              }
                              setTable(newTable);
                            }}
                            rawData={initTable}
                            setRawData={setInitTable}
                            stateKey='unit'
                            codeKey='cdName'
                            index={i}
                            object={el}
                            primaryKey='unit'
                            width='90%'
                            disabled={!auth.createAuth && !auth.updateAuth}
                          />
                        ) : (
                          <SelectBoxs
                            options={[{ type: 'unit', unit: (el as any).unit || getDefaultUnit(el.srCd), cdName: (el as any).unit || getDefaultUnit(el.srCd) }]}
                            defaultOption={(el as any).unit || getDefaultUnit(el.srCd)}
                            state={table}
                            setState={setTable}
                            rawData={initTable}
                            setRawData={setInitTable}
                            stateKey='unit'
                            codeKey='cdName'
                            index={i}
                            object={el}
                            primaryKey='unit'
                            width='90%'
                            disabled
                          />
                        )}
                      </div>
                      <div className='trCol10 flex-center'>
                        <SelectBoxs
                          options={useYnComCdList}
                          defaultOption={el.useYn === 'Y' ? t('사용') : t('미사용')}
                          state={table}
                          setState={setTable}
                          rawData={initTable}
                          setRawData={setInitTable}
                          stateKey='useYn'
                          codeKey='cdName'
                          index={i}
                          object={el}
                          primaryKey='useYn'
                          width='90%'
                        />
                      </div>
                      {auth.deleteAuth && (
                        <div className='trCol6 flex-center shrink0 padding-right-05'>
                          <BtnRed onClick={() => onClickDelete(el, i)}>{isFirstInput ? t('제거') : t('삭제')}</BtnRed>
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })
            ) : (
              <div className='noData'>No data.</div>
            )}
          </div>
        </div>
      </InputTable>
      <ButtonsWrapper>{(auth.createAuth || auth.updateAuth) && <BtnBlue onClick={onClickSave}>{t('저장')}</BtnBlue>}</ButtonsWrapper>
      <Portal openModal={openModal?.status}>{openModal && <DeleteModal openModal={openModal} setOpenModal={setOpenModal} />}</Portal>
    </Root>
  );
};

export default SensorRangeSetting;
